'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, Zap, BarChart2, CheckCircle } from 'lucide-react';

const features = [
  { id: 'feat-ai', icon: <Zap size={14} />, text: '12 AI-powered trade ideas daily' },
  { id: 'feat-market', icon: <BarChart2 size={14} />, text: 'Indian & global market coverage' },
  { id: 'feat-risk', icon: <Shield size={14} />, text: 'Risk-managed entry, SL & targets' },
  { id: 'feat-beginner', icon: <CheckCircle size={14} />, text: 'Beginner-friendly explanations' },
];

export default function AuthPageClient() {
  const [tab, setTab] = useState<'login' | 'register'>('login');

  return (
    <div
      className="min-h-screen flex flex-col lg:flex-row hero-gradient"
      style={{ background: 'var(--background)' }}
    >
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 xl:p-16 lg:w-[480px] xl:w-[520px] flex-shrink-0 relative overflow-hidden">
        {/* Background decoration */}
        <div
          className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 20% 40%, rgba(0,212,170,0.08) 0%, transparent 60%)',
          }}
        />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={36} />
          <span className="font-semibold text-lg neon-text">Trade With Krish</span>
        </div>

        {/* Hero content */}
        <div className="relative z-10">
          <div
            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full mb-6"
            style={{ background: 'rgba(0,212,170,0.1)', color: 'var(--primary)', border: '1px solid rgba(0,212,170,0.2)' }}
          >
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'var(--primary)' }} />
            AI Scanner Active — 15 May 2026
          </div>

          <h1 className="text-3xl xl:text-4xl font-bold leading-tight mb-4">
            AI-Powered Trading Research for{' '}
            <span className="neon-text">Indian &amp; Global</span>{' '}
            Markets
          </h1>

          <p className="text-base mb-8" style={{ color: 'var(--muted-foreground)', lineHeight: 1.6 }}>
            Get daily high-probability trade ideas, AI-powered market analysis, and professional
            research — built for traders of all levels.
          </p>

          <div className="space-y-3 mb-10">
            {features?.map((f) => (
              <div key={f?.id} className="flex items-center gap-3">
                <span className="text-positive">{f?.icon}</span>
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {f?.text}
                </span>
              </div>
            ))}
          </div>

          {/* Fake stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { id: 'stat-users', val: '48K+', label: 'Active Traders' },
              { id: 'stat-wr', val: '76%', label: 'Avg Win Rate' },
              { id: 'stat-ideas', val: '12/day', label:'AI Ideas' },
            ]?.map((s) => (
              <div
                key={s?.id}
                className="p-3 rounded-xl text-center"
                style={{ background: 'var(--surface-3)', border: '1px solid var(--border)' }}
              >
                <p className="text-xl font-bold font-mono-nums neon-text">{s?.val}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{s?.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs relative z-10" style={{ color: 'var(--muted-foreground)' }}>
          Trading involves market risk. Please do your own research before investing. © Trade With Krish
        </p>
      </div>
      {/* Right Panel — Auth Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 min-h-screen lg:min-h-0">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={32} />
          <span className="font-semibold neon-text">Trade With Krish</span>
        </div>

        <div
          className="w-full max-w-md rounded-2xl p-8"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        >
          {/* Tab switcher */}
          <div
            className="flex rounded-xl p-1 mb-7"
            style={{ background: 'var(--surface-3)' }}
          >
            <button
              onClick={() => setTab('login')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                tab === 'login' ? 'text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
              }`}
              style={tab === 'login' ? { background: 'var(--card)' } : {}}
            >
              Sign In
            </button>
            <button
              onClick={() => setTab('register')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                tab === 'register' ? 'text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
              }`}
              style={tab === 'register' ? { background: 'var(--card)' } : {}}
            >
              Create Account
            </button>
          </div>

          {tab === 'login' ? (
            <LoginForm />
          ) : (
            <RegisterForm onSwitchToLogin={() => setTab('login')} />
          )}
        </div>

        {/* Plan teaser */}
        <div
          className="w-full max-w-md mt-4 p-4 rounded-xl flex items-center justify-between gap-4"
          style={{ background: 'rgba(0,212,170,0.05)', border: '1px solid rgba(0,212,170,0.15)' }}
        >
          <div>
            <p className="text-sm font-semibold">Upgrade to Pro</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              Unlimited signals · Live alerts · Advanced AI analysis
            </p>
          </div>
          <div className="flex-shrink-0">
            <span className="text-sm font-bold neon-text">₹999/mo</span>
          </div>
        </div>

        <p className="mt-6 text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
          © Trade With Krish · Trading involves market risk.
        </p>
      </div>
    </div>
  );
}